import { useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, Loader2, CheckCircle2, XCircle, FileText, SkipForward, Download, Eye } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ImportResponse } from "@/lib/api";
import { friendlyError } from "@/lib/errors";

export interface CsvImportCardProps {
  title: string;
  description: string;
  columns: string[];
  exampleCsv: string;
  filename: string;
  /** Import réel ou dry-run (aperçu) selon le second argument. */
  importFn: (csv: string, dryRun?: boolean) => Promise<ImportResponse>;
  invalidateQueries: (readonly string[] | string[])[];
  /** Désactive l'import (ex. année non active — Lot D). */
  disabled?: boolean;
}

/** Découpe le CSV en lignes non vides (en-tête inclus). */
function splitCsvLines(csv: string): string[] {
  return csv.split(/\r?\n/).filter((l) => l.trim().length > 0);
}

export default function CsvImportCard({
  title, description, columns, exampleCsv, filename, importFn, invalidateQueries, disabled = false,
}: CsvImportCardProps) {
  // Aperçu (dry-run) en attente de confirmation : contient le CSV brut + le résultat dry-run.
  const [preview, setPreview] = useState<{ csv: string; result: ImportResponse } | null>(null);
  // Résultat de l'import RÉEL (après confirmation).
  const [result, setResult] = useState<ImportResponse | null>(null);
  const [showExample, setShowExample] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  // Étape 1 — dry-run : parse+valide sans persister, on affiche l'aperçu.
  const dryRunMutation = useMutation({
    mutationFn: (csv: string) => importFn(csv, true),
    onSuccess: (data, csv) => {
      setPreview({ csv, result: data });
    },
    onError: (err: Error) => {
      toast.error(friendlyError(err));
    },
  });

  // Étape 2 — import réel (après « Confirmer l'import »).
  const importMutation = useMutation({
    mutationFn: (csv: string) => importFn(csv, false),
    onSuccess: (data) => {
      setPreview(null);
      setResult(data);
      invalidateQueries.forEach((k) => qc.invalidateQueries({ queryKey: k }));
      if (data.errors.length === 0 && data.created > 0) {
        toast.success(`${data.created} / ${data.total} importe(s) avec succes`);
      } else if (data.created > 0) {
        toast.warning(`${data.created} cree(s), ${data.skipped} ignore(s), ${data.errors.length} erreur(s)`);
      } else {
        toast.error("Aucune ligne importee. Verifiez le format CSV.");
      }
    },
    onError: (err: Error) => {
      toast.error(friendlyError(err));
    },
  });

  const busy = dryRunMutation.isPending || importMutation.isPending;

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setResult(null);
      setPreview(null);
      dryRunMutation.mutate(reader.result as string);
    };
    reader.readAsText(file);
    e.target.value = "";
  }

  function handleDownload() {
    const bom = "﻿";
    const blob = new Blob([bom + exampleCsv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Erreurs dry-run indexées par numéro de ligne (1-based, en-tête = ligne 1).
  const errorsByLine = new Map<number, string>();
  if (preview) {
    for (const e of preview.result.errors) errorsByLine.set(e.line, e.message);
  }
  // Découpe le CSV en table : ligne 1 = en-tête, suivantes = données. Chaque
  // ligne est splittée sur la virgule pour produire des cellules <td>.
  const previewLines = preview ? splitCsvLines(preview.csv) : [];
  const headerCells = previewLines.length > 0 ? previewLines[0].split(",") : [];
  const dataRows = previewLines.slice(1);

  return (
    <Card className="p-5 border border-slate-200 shadow-none">
      <h3 className="font-bold text-navy mb-1">{title}</h3>
      <p className="text-sm text-slate-600 mb-3 break-words">{description}</p>

      <div className="flex flex-wrap items-center gap-3 mb-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileRef.current?.click()}
          disabled={busy || disabled}
          data-testid="csv-choose-file"
        >
          {dryRunMutation.isPending
            ? <><Loader2 size={14} className="animate-spin" /> Analyse en cours...</>
            : <><Upload size={14} /> Choisir un fichier CSV</>
          }
        </Button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} />
        <Button variant="ghost" size="sm" onClick={handleDownload}>
          <Download size={14} className="mr-1" />
          Télécharger le modèle
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setShowExample(!showExample)}>
          <FileText size={14} className="mr-1" />
          {showExample ? "Masquer" : "Voir"} l'exemple
        </Button>
      </div>

      {showExample && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="text-xs font-semibold text-slate-500 mb-1">
            Colonnes : {columns.join(", ")}
          </div>
          <pre className="text-xs text-slate-700 whitespace-pre-wrap break-words font-mono">{exampleCsv}</pre>
        </div>
      )}

      {preview && (
        <div className="space-y-3" data-testid="csv-preview">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-navy">
            <Eye size={15} /> Aperçu avant import
          </div>
          <div className="flex flex-wrap gap-3 text-sm">
            <span className="inline-flex items-center gap-1 text-green-700">
              <CheckCircle2 size={14} /> Créerait {preview.result.created}
            </span>
            <span className="inline-flex items-center gap-1 text-amber-700">
              <SkipForward size={14} /> Ignorerait {preview.result.skipped}
            </span>
            <span className="inline-flex items-center gap-1 text-red-600">
              <XCircle size={14} /> {preview.result.errors.length} erreur(s)
            </span>
          </div>

          <div className="max-h-64 overflow-auto rounded-lg border border-slate-200">
            <table className="w-full text-xs whitespace-nowrap">
              <thead className="bg-slate-100 font-semibold text-slate-600">
                <tr data-testid="csv-row-1">
                  <th className="px-2 py-1 text-left text-slate-400 font-mono w-8">#</th>
                  {headerCells.map((cell, c) => (
                    <th key={c} className="px-2 py-1 text-left font-mono">{cell}</th>
                  ))}
                  <th className="px-2 py-1 text-left text-red-700">Erreur</th>
                </tr>
              </thead>
              <tbody>
                {dataRows.map((line, i) => {
                  // dataRows[0] = ligne 2 du CSV. Erreurs dry-run sont 1-based.
                  const lineNo = i + 2;
                  const err = errorsByLine.get(lineNo);
                  const cells = line.split(",");
                  return (
                    <tr
                      key={lineNo}
                      data-testid={`csv-row-${lineNo}`}
                      className={err ? "bg-red-50 text-red-800" : "text-slate-700"}
                    >
                      <td className="px-2 py-1 text-slate-400 font-mono align-top w-8">{lineNo}</td>
                      {cells.map((cell, c) => (
                        <td key={c} className="px-2 py-1 font-mono align-top">{cell}</td>
                      ))}
                      <td className="px-2 py-1 text-red-700 align-top">
                        {err ? <span data-testid={`csv-row-error-${lineNo}`}>{err}</span> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              className="bg-navy text-white hover:bg-navy/90"
              disabled={busy || disabled}
              onClick={() => importMutation.mutate(preview.csv)}
              data-testid="csv-confirm-import"
            >
              {importMutation.isPending
                ? <><Loader2 size={14} className="animate-spin mr-1" /> Import en cours...</>
                : <>Confirmer l'import</>
              }
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => setPreview(null)}
              data-testid="csv-cancel-import"
            >
              Annuler
            </Button>
          </div>
        </div>
      )}

      {result && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-3 text-sm">
            <span className="inline-flex items-center gap-1 text-green-700">
              <CheckCircle2 size={14} /> {result.created} créé(s)
            </span>
            {result.skipped > 0 && (
              <span className="inline-flex items-center gap-1 text-amber-700">
                <SkipForward size={14} /> {result.skipped} ignoré(s) (déjà existant)
              </span>
            )}
            {result.errors.length > 0 && (
              <span className="inline-flex items-center gap-1 text-red-600">
                <XCircle size={14} /> {result.errors.length} erreur(s)
              </span>
            )}
          </div>
          {result.errors.length > 0 && (
            <div className="max-h-40 overflow-y-auto rounded-lg border border-red-200 bg-red-50 p-3 text-xs">
              {result.errors.map((e, i) => (
                <div key={i} className="text-red-800">
                  Ligne {e.line} : {e.message}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
